import React, { useEffect, useState } from "react";
import { Home, Newspaper, QrCode, Receipt, Settings, User } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { apiClient } from "../../lib/apiClient";
import { BrandLogo } from "../shared/BrandLogo";
import { NotificationBell } from "./NotificationBell";
import { HelpButton } from "./HelpButton";
import { CustomerAppPrompts } from "./CustomerAppPrompts";

const ALL_TABS = [
  { path: "/customer", label: "Home", icon: Home, testId: "customer-nav-home" },
  { path: "/customer/updates", label: "Updates", icon: Newspaper, testId: "customer-nav-updates" },
  { path: "/customer/qr", label: "QR", icon: QrCode, testId: "customer-nav-qr", pickupOnly: true },
  { path: "/customer/payments", label: "Payments", icon: Receipt, testId: "customer-nav-payments" },
  { path: "/customer/profile", label: "Settings", icon: Settings, testId: "customer-nav-profile" },
];

const FRAME_WIDTH = "mx-auto max-w-md md:max-w-xl lg:max-w-2xl";

export function CustomerShell({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  // Only used to decide whether the QR tab makes sense for this customer —
  // a delivery-only customer has no pickup QR pass. Default to "unknown"
  // (tab stays visible) until /customer/me resolves, since most pages
  // already call this same endpoint independently for their own data.
  const [customerType, setCustomerType] = useState(null);
  const [customerName, setCustomerName] = useState("");

  useEffect(() => {
    apiClient
      .get("/customer/me")
      .then(({ data }) => {
        setCustomerType(data.type ?? null);
        setCustomerName(`${data.firstname || ""} ${data.lastname || ""}`.trim());
        // Native push bridge: tell the Expo shell which customer is logged in so
        // it can register this device's push token. No-op in a normal browser.
        if (window.ReactNativeWebView && data.customer_id) {
          window.ReactNativeWebView.postMessage(
            JSON.stringify({ type: "vt_push_identity", user_id: String(data.customer_id) })
          );
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const link = document.getElementById("app-manifest");
    if (link) link.setAttribute("href", "/customer-manifest.json");
    return () => {
      if (link) link.setAttribute("href", "/manifest.json");
    };
  }, []);

  const tabs = ALL_TABS.filter((t) => !(t.pickupOnly && customerType === "2"));

  return (
    <div className="min-h-screen bg-background" data-testid="customer-shell">
      <header
        className={`sticky top-0 z-40 flex items-center justify-center border-b border-white/10 bg-primary px-4 py-3.5 md:border-x md:border-white/10 ${FRAME_WIDTH}`}
        data-testid="customer-header"
      >
        <HelpButton />
        <BrandLogo className="h-8" testId="customer-header-logo" />
        <NotificationBell />
      </header>
      {customerName ? (
        <div className={`flex justify-center px-4 pt-3 ${FRAME_WIDTH}`} data-testid="customer-name-pill-row">
          <span
            className="inline-flex max-w-full items-center gap-1.5 truncate rounded-full border border-border bg-surface px-3.5 py-1.5 text-sm font-semibold text-text-main shadow-sm"
            data-testid="customer-name-pill"
          >
            <User size={13} strokeWidth={2} className="shrink-0 text-primary" />
            {customerName}
          </span>
        </div>
      ) : null}
      <CustomerAppPrompts />
      <main
        className={`bg-background px-4 py-5 pb-[calc(7rem+env(safe-area-inset-bottom))] md:border-x md:border-border ${FRAME_WIDTH}`}
        data-testid="customer-main-content"
      >
        {children}
      </main>
      <nav
        className={`fixed inset-x-0 bottom-0 z-50 flex border-t border-border bg-surface/95 pb-[env(safe-area-inset-bottom)] backdrop-blur-lg ${FRAME_WIDTH}`}
        data-testid="customer-bottom-nav"
      >
        {tabs.map((tab) => {
          const active = location.pathname === tab.path;
          const Icon = tab.icon;
          return (
            <button
              key={tab.path}
              type="button"
              onClick={() => navigate(tab.path)}
              className={`flex min-h-[56px] flex-1 flex-col items-center justify-center gap-1 py-2 text-[11px] font-medium transition-all active:scale-95 ${
                active ? "text-primary font-semibold" : "text-text-muted/70"
              }`}
              data-testid={tab.testId}
            >
              <Icon size={20} strokeWidth={active ? 2 : 1.5} className={active ? "scale-110 transition-transform" : "transition-transform"} />
              {tab.label}
            </button>
          );
        })}
      </nav>
    </div>
  );
}
